import { FC, Fragment, useCallback, useState } from 'react'
import { Transition } from '@headlessui/react'

import { Icon } from '~/src/components/generic/PhosphorIcons'
import IconButton from '~/src/components/generic/IconButton'
import { Position, ToastProps } from '~/src/components/generic/Toast/types'
import {
  transitionClasses,
  typeColorMap,
  iconMap,
} from '~/src/components/generic/Toast/constants'
import useToast from '~/src/components/generic/Toast/hook/useToast'

import useTimeout from '~/hooks/useTimeout'

import { tw } from '~/lib/utils'

type Props = {
  toast: ToastProps
  delay: number
  showCloseButton: boolean
  position: Position
  style?: { container: string; content: string }
}

const Toast: FC<Props> = ({ toast, delay, showCloseButton, position, style }) => {
  const [show, setShow] = useState(true)
  const notify = useToast()

  useTimeout(() => setShow(false), delay)

  const handleClose = useCallback(() => {
    toast.onClose?.()
    notify?.remove(toast.id)
  }, [toast, notify])

  return (
    <Transition
      afterLeave={handleClose}
      appear
      as={Fragment}
      show={show}
      {...transitionClasses[position]}
    >
      <div
        className={tw(
          'relative w-full rounded p-3 shadow-md',
          typeColorMap[toast.type],
          style?.container
        )}
        role="alert"
      >
        <div className="flex items-start justify-between space-x-2">
          <div className={tw('flex space-x-2', style?.content)}>
            <div>{iconMap[toast.type]}</div>
            <p>{toast.message}</p>
          </div>
          {showCloseButton && (
            <IconButton
              aria-label="Close toast window"
              variant="ghost"
              onClick={() => setShow(false)}
            >
              <Icon name="XIcon" size="20" weight="bold" />
            </IconButton>
          )}
        </div>
      </div>
    </Transition>
  )
}

export default Toast
